import { useState } from "react";
import { useIntl } from "react-intl";
import { LocalizedError } from "@/lib/localized-error";
import {
  companyDraftError,
  draftFromCompany,
  EMPTY_COMPANY_DRAFT,
  toCompanyProfileInput,
  type CompanyProfileDraft,
  type CompanyProfileSource,
} from "./company-profile-draft";
import type { UseCompanyAdminResult } from "./use-company-admin";

// Editing the company's own fields, WITHOUT a form of its own.
//
// This used to be CompanyProfileEditor: a section appended below /c/:id that re-drew, as four
// labelled inputs, the identity the page was already showing above it. The name appeared twice on
// one screen, the industry twice, the size twice, and the "About us" text twice — once as the
// company and once as a field about the company. The section is gone; what it KNEW is here, and the
// page renders each field where its value already is (the header, the About us box).
//
// EVERYTHING THE SECTION OWED THE APP IS STILL OWED. Only the chrome went:
//
//   - The draft is still ANNOUNCED, not just held. Three things in spark can destroy it without
//     asking: navigating away, closing the tab, and changing the acting-company context (which
//     empties the Apollo cache per @adr SPARK-0034-acting-company-context-resets-the-apollo-cache@1 and remounts the page). All three consult the
//     PendingEdits registry, so the caller registers COMPANY_SCOPE_PROFILE around this — see
//     CompanyPage, which mounts the registration for exactly as long as the page is in edit mode.
//   - `saveOrThrow` still REJECTS with a LocalizedError. The registry's callers (the SaveBar,
//     UnsavedGuard, the acting-company gate) read a rejection as "do not proceed", and the dialog
//     prints the message, so it has to be spark's own translated copy and never genesis's prose
//     (@adr SPARK-0010-no-raw-server-errors-in-ui@1).
//   - companyDraftError still runs BEFORE the round trip: a name is required, the count is a whole
//     number of 0 or more, and both length caps hold. The in-place fields carry no native
//     `maxLength` (InlineEdit refuses it on purpose, because it swallows keystrokes and truncates a
//     paste silently), so the save gate is the only thing left that can say no in the browser.
//
// AND THE REGISTRY IS STILL THE ONLY SAVE. The section carried a "Save profile" / "Discard changes"
// pair once, doing exactly what the SaveBar the same scope raises already does; a reviewer had it
// removed because two controls for one operation make the reader guess which is real. Nothing here
// hands one back — this hook exposes no button, only the operations the bar drives.
//
// THE DRAFT IS NULL UNTIL SOMETHING IS TYPED, and that is load-bearing rather than tidy. The hook is
// now called from the page, which renders before the company query has answered and re-renders every
// time a write refetches it. A draft seeded once from whatever `company` happened to be on the first
// render would be seeded from nothing at all — and would then read as an edit that blanks the
// company. While the draft is null the fields simply show what genesis holds, live.

/** The state and the operations the page needs to edit a company in place. */
export interface CompanyProfileEditing {
  /** What the fields show: the admin's draft once there is one, otherwise the stored values. */
  draft: CompanyProfileDraft;
  /** Stage one field. Nothing reaches genesis until the bar saves. */
  set: (field: keyof CompanyProfileDraft, next: string) => void;
  /** True while the draft differs from what genesis holds — what the SaveBar reads. */
  dirty: boolean;
  /** A refusal, already localized: either this side's or genesis's, mapped by use-company-admin. */
  message: string | null;
  /** True for as long as the last successful save is the last thing that happened. */
  saved: boolean;
  saveOrThrow: () => Promise<void>;
  discard: () => void;
}

export function useCompanyProfileEditing(
  /** The stored company, or nothing while the page's query is still in flight. */
  company: CompanyProfileSource | null | undefined,
  admin: UseCompanyAdminResult,
): CompanyProfileEditing {
  const intl = useIntl();
  const [typed, setTyped] = useState<CompanyProfileDraft | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const stored = company ? draftFromCompany(company) : EMPTY_COMPANY_DRAFT;
  const draft = typed ?? stored;
  const dirty =
    typed !== null &&
    (Object.keys(stored) as Array<keyof CompanyProfileDraft>).some(
      (field) => typed[field] !== stored[field],
    );

  function set(field: keyof CompanyProfileDraft, next: string) {
    setTyped((current) => ({ ...(current ?? stored), [field]: next }));
    setSaved(false);
  }

  async function save(): Promise<{ ok: true } | { ok: false; message: string }> {
    // Nothing typed, or typed back to what genesis already holds: the guards save every scope they
    // are holding, including ones nobody touched, and a write that changes nothing is still a write.
    if (!dirty) return { ok: true };
    setSaved(false);
    const invalid = companyDraftError(intl, draft);
    if (invalid) {
      setMessage(invalid);
      return { ok: false, message: invalid };
    }
    setMessage(null);
    const input = toCompanyProfileInput(draft);
    const result = await admin.saveProfile(input);
    if (result.ok) {
      setSaved(true);
      // Settle on what was actually SENT, which is the trimmed form of what was typed. A trailing
      // space left in a field would otherwise keep the draft different from the stored value for
      // good — a page that is permanently dirty asks about unsaved edits that do not exist, and the
      // question stops being read.
      setTyped({
        name: input.name,
        industryId: input.industryId ?? "",
        employeeCount: input.employeeCount == null ? "" : String(input.employeeCount),
        summary: input.summary ?? "",
      });
      return { ok: true };
    }
    setMessage(result.message);
    return { ok: false, message: result.message };
  }

  /**
   * @throws when the draft is refused, whether by this side or by genesis. Whoever is holding a
   *   navigation reads a rejection as "do not proceed": a switch that swallowed the refusal would
   *   destroy the very edit the reader asked to keep.
   */
  async function saveOrThrow(): Promise<void> {
    const result = await save();
    if (!result.ok) throw new LocalizedError(result.message);
  }

  function discard() {
    setTyped(null);
    setMessage(null);
    setSaved(false);
  }

  return { draft, set, dirty, message, saved, saveOrThrow, discard };
}
